import { React, useEffect, useState } from 'react';
import axios from '../../../../api/myAxios';
import {
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  useTheme,
  Box,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Fade,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Link as LinkIcon,
  TextFields as TextFieldsIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const DataList = ({ workspaceId, refetch, onEditItem }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [dataItemToDelete, setDataItemToDelete] = useState(null);
  const [deleteSuccess, setDeleteSuccess] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  const {
    data: dataItems = [],
    isLoading,
    error,
    refetch: refetchData,
  } = useQuery({
    queryKey: ['dataItems', workspaceId],
    queryFn: async () => {
      const response = await axios.get(`/api/data/${workspaceId}`);
      return response.data;
    },
    enabled: !!workspaceId,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    gcTime: 1000 * 60 * 10,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  useEffect(() => {
    if (deleteSuccess) {
      const timer = setTimeout(() => setDeleteSuccess(''), 2000);
      return () => clearTimeout(timer);
    }
  }, [deleteSuccess]);

  const handleMenuOpen = (e, item) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setSelectedItem(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedItem(null);
  };

  const handleDelete = () => {
    setDataItemToDelete(selectedItem._id);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleEdit = () => {
    if (
      onEditItem &&
      (selectedItem.type === 'text' || selectedItem.type === 'url')
    ) {
      onEditItem(selectedItem);
    }
    handleMenuClose();
  };

  const handleDeleteConfirm = () => {
    if (dataItemToDelete) {
      deleteDataItemMutation.mutate(dataItemToDelete);
      setDeleteDialogOpen(false);
      setDataItemToDelete(null);
    }
  };

  const deleteDataItemMutation = useMutation({
    mutationFn: async (id) => {
      await axios.delete(`/api/data/${id}`);
    },
    onSuccess: (_, id) => {
      queryClient.setQueryData(['dataItems', workspaceId], (oldData) => {
        if (!oldData) return oldData;
        return oldData.filter((item) => item._id !== id);
      });
      queryClient.invalidateQueries({ queryKey: ['dataItems', workspaceId] });
      setDeleteSuccess(t('dataList.deleteSuccess'));
    },
    onError: (error) => {
      console.error(t('error.console.errorDeletingDataItem'), error);
    },
  });

  // const handleUpdateConfirm = () => {
  //   if (dataItemToUpdate) {
  //     updateDataItemMutation.mutate(dataItemToUpdate);
  //     setUpdateDialogOpen(false);
  //     setDataItemToUpdate(null);
  //   }
  // };

  useEffect(() => {
    if (refetch && workspaceId) {
      refetchData();
    }
  }, [refetch, refetchData, workspaceId]);

  const getDataIcon = (type) => {
    switch (type) {
      case 'file':
        return <DescriptionIcon />;
      case 'url':
        return <LinkIcon />;
      case 'text':
        return <TextFieldsIcon />;
      default:
        return <DescriptionIcon />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <Paper variant="outlined" sx={{ mt: 3, p: 4, textAlign: 'center' }}>
        <CircularProgress size={40} />
        <Typography variant="body2" sx={{ mt: 2 }}>
          {t('dataList.loading')}
        </Typography>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper variant="outlined" sx={{ mt: 3 }}>
        <Typography
          variant="h6"
          sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}
        >
          {t('dataList.title')}
        </Typography>
        <Alert severity="error" sx={{ m: 2 }}>
          {t('dataList.fetchingErrorMessage')}
          <br />
          <small>{error.message}</small>
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ mt: 3 }}>
      <Typography
        variant="h6"
        sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}
      >
        {t('dataList.title')}
        {deleteDataItemMutation.isLoading && (
          <CircularProgress size={16} sx={{ ml: 2 }} />
        )}
      </Typography>
      {deleteSuccess && (
        <Fade in={deleteSuccess !== ''} timeout={1000}>
          <Alert severity="success" sx={{ width: '100%', mb: 3 }}>
            {deleteSuccess}
          </Alert>
        </Fade>
      )}
      <List sx={{ maxHeight: '400px', overflowY: 'auto' }}>
        {dataItems.map((item, index) => (
          <ListItem
            key={item._id}
            sx={{
              borderBottom:
                index < dataItems.length - 1
                  ? `1px solid ${theme.palette.divider}`
                  : 'none',
              opacity:
                deleteDataItemMutation.isLoading &&
                deleteDataItemMutation.variables === item._id
                  ? 0.5
                  : 1,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <ListItemIcon>{getDataIcon(item.type)}</ListItemIcon>
            <ListItemText
              primary={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant="body1">
                    {item.originalName ||
                      item.description ||
                      t('dataList.noDescription')}
                  </Typography>
                  <IconButton
                    edge="end"
                    aria-label="options"
                    onClick={(e) => handleMenuOpen(e, item)}
                    size="small"
                    disabled={deleteDataItemMutation.isLoading}
                  >
                    {deleteDataItemMutation.isLoading &&
                    deleteDataItemMutation.variables === item._id ? (
                      <CircularProgress size={16} />
                    ) : (
                      <MoreVertIcon />
                    )}
                  </IconButton>
                </Box>
              }
              secondary={`${t(`dataList.${item.type}`)} • ${t('dataList.addedAt')} ${formatDate(item.createdAt)}`}
            />
          </ListItem>
        ))}
        {dataItems.length === 0 && (
          <ListItem>
            <ListItemText
              primary={t('dataList.noData')}
              secondary={t('dataList.noDataDescription')}
              sx={{ textAlign: 'center', py: 4 }}
            />
          </ListItem>
        )}
      </List>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1,
            minWidth: 120,
          },
        }}
      >
        {selectedItem &&
          (selectedItem.type === 'text' || selectedItem.type === 'url') && (
            <MenuItem onClick={handleEdit}>
              <EditIcon sx={{ mr: 1, fontSize: 20 }} />
              {t('dataList.edit')}
            </MenuItem>
          )}
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1, fontSize: 20 }} />
          {t('dataList.delete')}
        </MenuItem>
      </Menu>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>{t('workspace.confirmation')}</DialogTitle>
        <DialogContent>{t('dataList.deleteConfirmation')}</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {t('workspace.cancel')}
          </Button>
          <Button onClick={handleDeleteConfirm} color="error">
            {t('workspace.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default DataList;
